import Link from "next/link";
import { useRouter } from "next/router";
import { BiMap } from "react-icons/bi";
import { BsGrid } from "react-icons/bs";
import FilterByButton from "./filters/filter-by-button";
import { useTranslation } from "@hooks/useTranslation";
import { type ExperiencesFilterOptions } from "./experiences-filters";
import { useState } from "react";
import FilterButton from "./filters/filter-button";
import { MdClose } from "react-icons/md";
import { routes } from "@lib/constants";

import Tabs from "@components/spotprofile/tabs";

export default function FiltersBar({
  text,
  count,
  selectedFilters,
  onFiltersChange,
}: {
  text: string;
  count: number;
  selectedFilters?: ExperiencesFilterOptions;
  onFiltersChange?: (filters: ExperiencesFilterOptions) => void;
}) {
  const router = useRouter();
  const isInMap = router.pathname.endsWith("map");

  const { t } = useTranslation();

  const [showAllFilter, setShowAllFilter] = useState(false);

  const prevFilters = selectedFilters
    ? Object.entries(selectedFilters)
        .filter(([, v]) => v !== undefined)
        .reduce<any[]>((prev, [k, v]) => {
          if (Array.isArray(v)) {
            return [...prev, ...v.map((filter) => ({ filter, k }))];
          }
          return [...prev, { filter: v, k }];
        }, [])
    : [];

  const removeFilter = (key: string, value: any) => {
    if (onFiltersChange && selectedFilters) {
      onFiltersChange({
        ...selectedFilters,
        [key]: Array.isArray((selectedFilters as any)[key])
          ? (selectedFilters as any)[key]?.filter((v: any) => v.id !== value.id)
          : undefined,
      });
    }
  };

  return (
    <div className="w-full z-20 sticky top-11 mt-0 lg:mt-0 md:top-[69px] py-2 lg:py-0.5 md:px-[5%] bg-black md:bg-white ">
      <div className={`flex items-center justify-between w-full px-4 z-50`}>
        <div className="w-1/3">
          {!router.pathname.endsWith("map") && (
            <div className="flex flex-col justify-center w-fit">
              <h3 className="flex items-center pb-2 text-[12px] md:text-base font-bold text-center justify-evenly text-primary-color">
                {count}
                <span className="ml-1 font-normal text-base text-white md:text-black whitespace-nowrap">
                  {text}
                </span>
              </h3>
              <FilterByButton arrow="down" />
            </div>
          )}
        </div>

        <div className="hidden lg:block w-2/3">{!isInMap && <Tabs />}</div>
        <div className="flex items-center justify-end gap-1 w-1/3">
          <Link
            href={
              isInMap
                ? `/spotprofile/${router.query.id}/${routes.placesToVisit}`
                : router.asPath
            }
            className={`flex justify-center items-center gap-1 mx-1 pb-1 transition border-b-2 text-white md:text-black ${
              !isInMap
                ? "border-primary-color"
                : "border-transparent text-opacity-80"
            }`}
          >
            <BsGrid className="w-7 h-7" />
            <p className="hidden text-xs md:block">
              {t("spotprofile.gallery_view")}
            </p>
          </Link>
          <Link
            href={`/spotprofile/${router.query.id}/map`}
            className={`flex justify-center items-center gap-1 mx-1 pb-1 transition border-b-2 text-white md:text-black ${
              isInMap
                ? "border-primary-color"
                : "border-transparent text-opacity-80"
            }`}
          >
            <BiMap className="w-7 h-7" />
            <p className="hidden text-xs md:block">
              {t("spotprofile.map.title")}
            </p>
          </Link>
        </div>
      </div>
      {prevFilters.length > 0 && (
        <div
          className={`flex ${
            showAllFilter ? "flex-wrap" : ""
          } w-full gap-x-1 px-4 pt-4 text-black`}
        >
          {prevFilters
            .slice(0, showAllFilter ? prevFilters.length : 2)
            .map(({ filter, k }) => (
              <FilterButton
                key={k + filter.id}
                isActive={false}
                onClick={() => removeFilter(k, filter)}
                className="!border-primary-color/50 items-center"
              >
                <p className="px-1 text-xs">{filter.value}</p>
                <MdClose className="text-2xl" />
              </FilterButton>
            ))}

          {prevFilters.length > 2 && !showAllFilter && (
            <FilterButton
              isActive={false}
              onClick={() => setShowAllFilter(true)}
              className="!border-primary-color/50 items-center"
            >
              <p className="h-full px-1">...</p>
            </FilterButton>
          )}
        </div>
      )}
    </div>
  );
}
